'use client';

import { ComponentType, MouseEvent, SVGProps, useState } from 'react';

import {
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import clsx from 'clsx';
import { useLocale, useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';

import SwipeableModal from '@/components/ModalRoot/SwipeableModal';
import { Croatia, English } from '@/components/SvgIcons/Flags';
import World from '@/components/SvgIcons/World';
import { getTranslatedBlogSlug } from '@/config/slugs.config';
import { usePathname, useRouter } from '@/i18n/navigation';
import { Locale } from '@/types/language.type';

import styles from './LanguagePicker.module.scss';

interface FlagIconProps {
  props?: SVGProps<SVGSVGElement>;
  width?: string | number;
  height?: string | number;
}

type LanguageDetails = { code: Locale; labelKey: 'hr' | 'en'; Icon: ComponentType<FlagIconProps> };

const languages: LanguageDetails[] = [
  { code: 'hr', labelKey: 'hr', Icon: Croatia },
  { code: 'en', labelKey: 'en', Icon: English },
];

const LanguagePicker = () => {
  const t = useTranslations('common');
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const params = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const menuOpen = Boolean(anchorEl);

  const handleButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (isMobile) {
      setMobileOpen(true);
    } else if (menuOpen) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMobileClose = () => {
    setMobileOpen(false);
  };

  const handleMobileOpen = () => {
    setMobileOpen(true);
  };

  const handleLanguageSelect = (selectedLocale: Locale) => {
    const { slug } = params as { slug?: string };

    if (slug) {
      const isBlogPage = pathname.startsWith('/blog/');
      let targetSlug = slug;

      if (isBlogPage) {
        // slug is always EN version (next-intl rewrites localized URLs to internal EN paths)
        targetSlug = getTranslatedBlogSlug(slug, selectedLocale, 'en');
      }

      router.replace(
        { pathname: pathname as '/blog/[slug]', params: { slug: targetSlug } },
        { locale: selectedLocale }
      );
    } else {
      router.replace(pathname as '/', { locale: selectedLocale });
    }

    handleMenuClose();
    handleMobileClose();
  };

  return (
    <>
      <IconButton
        aria-label={t('languagePicker.ariaLabel')}
        onClick={handleButtonClick}
        classes={{ root: styles.iconButtonRoot }}
        className={styles.iconButton}
      >
        <World />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        classes={{ paper: styles.menuPaper }}
        disableScrollLock
        slotProps={{
          root: {
            style: { zIndex: 1500 },
          },
        }}
      >
        {languages.map(lang => (
          <ListItem key={lang.code} disablePadding>
            <ListItemButton
              selected={locale === lang.code}
              onClick={() => handleLanguageSelect(lang.code)}
              classes={{ root: styles.listItemRoot }}
              className={styles.listItem}
            >
              <ListItemIcon className={styles.listItemIcon}>
                <lang.Icon />
              </ListItemIcon>
              <ListItemText>
                <Typography variant="body1" color="inherit">
                  {t(`languagePicker.languages.${lang.labelKey}`)}
                </Typography>
              </ListItemText>
            </ListItemButton>
          </ListItem>
        ))}
      </Menu>
      <SwipeableModal
        open={mobileOpen}
        onOpen={handleMobileOpen}
        onClose={handleMobileClose}
        title={t('languagePicker.title')}
        hideConfirmButton
        hideCancelButton
      >
        <List disablePadding classes={{ root: styles.root }} className={styles.list}>
          {languages.map(lang => (
            <ListItem key={lang.code} disablePadding>
              <ListItemButton
                selected={locale === lang.code}
                onClick={() => handleLanguageSelect(lang.code)}
                classes={{ root: styles.listItemRoot }}
                className={clsx(styles.listItem, styles.listItemMobile)}
              >
                <ListItemIcon className={styles.listItemIcon}>
                  <lang.Icon />
                </ListItemIcon>
                <ListItemText>
                  <Typography variant="subtitle1" component="p" fontWeight={400} color="inherit">
                    {t(`languagePicker.languages.${lang.labelKey}`)}
                  </Typography>
                </ListItemText>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </SwipeableModal>
    </>
  );
};

export default LanguagePicker;
